<template>
  <nav class="fixed bottom-0 w-full max-w-[800px] z-50 bg-white/95 backdrop-blur-xl rounded-t-[32px] border-t border-slate-100/90 shadow-[0_-12px_32px_rgba(0,0,0,0.06)] pb-[max(16px,env(safe-area-inset-bottom))]">
    <div class="relative pt-2 pb-1">
      <ul class="grid grid-flow-col justify-between" :class="`grid-cols-${navbar.length}`">
        <NavLink v-for="(nav, index) in navbar" 
          :key="index" 
          :to="{ name: nav.name }" 
          :icon="nav.icon" 
          :label="nav.label" 
          :custom-class="nav.customClass" 
          :notif="nav.notif"
          :newTab="nav.newTab"
        />
      </ul>
      <!-- Modern Floating Capsule Indicator -->
      <div 
        v-if="currentIndex !== null && currentIndex >= 0"
        class="nav-indicator-pill"
        :style="{ left: `calc(${currentIndex * (100 / navbar.length)}% + ${100 / navbar.length / 2}%)` }"
      ></div>
    </div>
  </nav>
</template>

<script>
import { useRoute } from 'vue-router';
import { ref, onMounted, watch, computed } from 'vue';
import NavLink from './NavLink.vue';
import { useProfileStore } from '@/stores/useProfileStore';
import { RoleEnum } from '@/enums/RoleEnum';

export default {
  name: 'NavBar',
  components: { NavLink },
  setup() {
    const currentIndex = ref(null);
    const route = useRoute();
    const profileStore = useProfileStore();

    const isTeacher = computed(() => {
      if (profileStore.profile?.roles?.some(r => r.name === RoleEnum.TEACHER || r.name === 'teacher' || r.name === 'Teacher')) return true;
      try {
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        if (user?.roles?.some(r => r.name === RoleEnum.TEACHER || r.name === 'teacher' || r.name === 'Teacher')) return true;
        if (user?.role === 'teacher' || user?.role_name === 'teacher') return true;
      } catch (err) {
        console.warn('Gagal membaca role guru dari cache user:', err);
      }
      return false;
    });

    const teacherNavbar = computed(() => [
      { name: 'TeacherHome', icon: ['fas', 'house'], label: 'Beranda', notif: 0, newTab: false },
      { name: 'TeacherAttendance', icon: ['fas', 'clipboard-check'], label: 'Absensi', notif: 0, newTab: false },
      { name: 'QRCodeScanner', icon: ['fas', 'qrcode'], label: 'Pay', customClass: 'QR', notif: 0, newTab: false },
      { name: 'QAChat', icon: ['fas', 'comments'], label: 'Chat', notif: 0, newTab: false },
      { name: 'Profile', icon: ['fas', 'user'], label: 'Profil', notif: 0, newTab: false },
    ]);

    const studentNavbar = computed(() => [
      { name: 'Home', icon: ['fas', 'house'], label: 'Home', notif: 0, newTab: false },
      { name: 'Publication', icon: ['fas', 'newspaper'], label: 'News', notif: 0, newTab: false },
      { name: 'QRCodeScanner', icon: ['fas', 'qrcode'], label: 'Pay', customClass: 'QR', notif: 0, newTab: false },
      { name: 'Academic', icon: ['fas', 'graduation-cap'], label: 'Edu', notif: 0, newTab: false },
      { name: 'Profile', icon: ['fas', 'user'], label: 'Profile', notif: 0, newTab: false },
    ]);

    const navbar = computed(() => isTeacher.value ? teacherNavbar.value : studentNavbar.value);

    const teacherRouteMap = {
      TeacherHome: (name, path) => name === 'TeacherHome' || path === '/teacher-home',
      TeacherAttendance: (name, path) => name.startsWith('TeacherAttendance') || path.startsWith('/teacher-attendance'),
      TeacherSchedule: (name, path) => name === 'TeacherSchedule' || path.startsWith('/teacher-schedule') || path.startsWith('/schedule'),
      QRCodeScanner: (name) => name === 'QRCodeScanner',
      QAChat: (name, path) => name === 'QAChat' || name === 'GroupChatRoom' || path.startsWith('/quick-access/chat'),
      Profile: (name, path) => name.startsWith('Profile') || path.startsWith('/profile'),
    };

    const studentRouteMap = {
      Home: (name, path) => name === 'Home' || path === '/home',
      Publication: (name, path) => name.startsWith('Publication') || path.startsWith('/publication'),
      QRCodeScanner: (name) => name === 'QRCodeScanner',
      Academic: (name, path) => name.startsWith('Academic') || path.startsWith('/academic'),
      Profile: (name, path) => name.startsWith('Profile') || path.startsWith('/profile'),
    };

    const checkIndex = () => {
      const currentName = String(route.name || '');
      const currentPath = String(route.path || '');
      const routeMap = isTeacher.value ? teacherRouteMap : studentRouteMap;

      const foundIndex = navbar.value.findIndex(item => {
        const matcher = routeMap[item.name];
        return matcher ? matcher(currentName, currentPath) : item.name === currentName;
      });

      currentIndex.value = foundIndex;
    };

    onMounted(checkIndex);
    watch(() => route.name, checkIndex);
    watch(() => route.path, checkIndex);
    watch(isTeacher, checkIndex);

    return { navbar, currentIndex };
  },
};
</script>

<style scoped>
.nav-indicator-pill {
  position: absolute;
  top: 0;
  height: 3.5px;
  width: 38px;
  border-radius: 9999px;
  background-color: var(--primary-color, #005bbf);
  transform: translateX(-50%);
  pointer-events: none;
  box-shadow: 0 2px 8px rgba(0, 91, 191, 0.35);
  transition: left 0.32s cubic-bezier(0.34, 1.56, 0.64, 1);
  will-change: left;
}
</style>
